import React, { useMemo, useState } from 'react';
import { Checkbox, Input, Spin } from 'antd';
import clsx from 'clsx';
import { useFormContext, useWatch } from 'react-hook-form';
import ConditionalRender from '../../../../shared/components/ConditionalRender';
import { useOrgGroups } from '../../../../hooks/useOrgGroups';
import type { UserFormValues } from '../../../../types/UserManagementTypes';
import { ADD_GROUP_LABEL, GROUPS_STEP_NOTE, NEW_GROUP_PLACEHOLDER, STEP_HEADINGS } from './constants';
import styles from './UserFormModal.module.scss';

const GroupsStep = () => {
  const { groups, isLoadingGroups } = useOrgGroups();
  const { setValue, getValues } = useFormContext<UserFormValues>();
  const groupIds = useWatch<UserFormValues, 'groupIds'>({ name: 'groupIds' }) ?? [];
  const newGroupNames = useWatch<UserFormValues, 'newGroupNames'>({ name: 'newGroupNames' }) ?? [];

  // null closes the inline input; '' opens it empty.
  const [draftName, setDraftName] = useState<string | null>(null);

  const selectedIds = useMemo(() => new Set(groupIds), [groupIds]);
  const takenNames = useMemo(
    () => new Set([...groups.map((group) => group.name.toLowerCase()), ...newGroupNames.map((name) => name.toLowerCase())]),
    [groups, newGroupNames],
  );

  // Both writes read the live value rather than this render's copy, so adding a
  // group can never write a stale selection back over the other field. The ??
  // guards matter: getValues returns undefined for a field that is only
  // watched and has not been written yet, and spreading that throws.
  const toggleGroup = (id: string) => {
    const current = getValues('groupIds') ?? [];
    setValue('groupIds', current.includes(id) ? current.filter((selected) => selected !== id) : [...current, id], { shouldDirty: true });
  };

  // A new group exists only because it was selected, so clearing it discards it.
  const discardNewGroup = (name: string) => {
    const current = getValues('newGroupNames') ?? [];
    setValue(
      'newGroupNames',
      current.filter((existing) => existing !== name),
      { shouldDirty: true },
    );
  };

  const commitDraft = () => {
    const name = (draftName ?? '').trim();
    if (name && !takenNames.has(name.toLowerCase())) {
      setValue('newGroupNames', [...(getValues('newGroupNames') ?? []), name], { shouldDirty: true });
    }
    setDraftName(null);
  };

  const handleDraftKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commitDraft();
      return;
    }
    // Without this the modal's own Esc handler would close the whole wizard.
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setDraftName(null);
    }
  };

  return (
    <section className={styles.step}>
      <h3 className={styles.stepHeading}>{STEP_HEADINGS.GROUPS}</h3>
      <p className={styles.stepNote}>{GROUPS_STEP_NOTE}</p>

      <ConditionalRender
        visible={!isLoadingGroups}
        fallback={
          <div className={styles.groupsLoading}>
            <Spin />
          </div>
        }
      >
        <div className={styles.groupsPanel}>
          <div className={styles.groupScroll}>
            <ul className={styles.groupList} aria-label={STEP_HEADINGS.GROUPS}>
              {groups.map((group) => (
                <li key={group.id} className={styles.groupRow}>
                  <Checkbox className="custom-checkbox" checked={selectedIds.has(String(group.id))} onChange={() => toggleGroup(String(group.id))}>
                    <span className={styles.optionLabel}>{group.name}</span>
                  </Checkbox>
                </li>
              ))}
              {newGroupNames.map((name) => (
                <li key={name} className={clsx(styles.groupRow, styles.groupRowNew)}>
                  <Checkbox className="custom-checkbox" checked onChange={() => discardNewGroup(name)}>
                    <span className={styles.optionLabel}>{name}</span>
                  </Checkbox>
                </li>
              ))}
            </ul>
          </div>

          {/* Outside the scroller so it stays visible however long the list is. */}
          <div className={styles.addGroupRow}>
            <ConditionalRender
              visible={draftName === null}
              fallback={
                <Input
                  autoFocus
                  value={draftName ?? ''}
                  placeholder={NEW_GROUP_PLACEHOLDER}
                  aria-label={ADD_GROUP_LABEL}
                  className={styles.newGroupInput}
                  onChange={(event) => setDraftName(event.target.value)}
                  onKeyDown={handleDraftKeyDown}
                  onBlur={() => setDraftName(null)}
                />
              }
            >
              <button type="button" className={styles.addGroupButton} onClick={() => setDraftName('')}>
                {ADD_GROUP_LABEL}
              </button>
            </ConditionalRender>
          </div>
        </div>
      </ConditionalRender>
    </section>
  );
};

export default GroupsStep;
